<template>
  <b-layout :colors="$site.colors" :header-title="$t('pages.pass.confirm.title')" :header-back-route="{ path: '/pass/customer' }" :breadcrumbs="breadcrumbs">
    <b-container class="mt-12">
      <b-summary>
        <template #summary>
          <pass-detail />
        </template>
        <div>
          <b-alert show variant="warning">
            <h4 class="alert-heading">
              {{ $t('pages.pass.confirm.almostDone') }}
            </h4>
            <p v-html="$t('pages.pass.confirm.almostDoneMessage')" />
          </b-alert>

          <b-errors :errors="errors" class="mb-4" />

          <b-card
            :title="$t('pages.pass.confirm.passDetails')"
            class="mb-6"
          >
            <p class="text-sm text-secondary mb-1">
              <strong>{{ $t('general.purchaser') }}:</strong> {{ customer.displayName }} ({{ customer.email }})
            </p>

            <p class="text-sm text-secondary">
              <strong>{{ $t('general.price') }}:</strong> {{ pass.priceLabel }}
            </p>
          </b-card>

          <b-card
            v-if="response && response.terms.required"
            ref="terms"
            :title="response.terms.title"
            class="mb-8"
          >
            <b-html :html="response.terms.content" class="mb-4 text-sm text-secondary" />

            <BCheckbox v-model="confirmData.termsAccepted" :label="response.terms.acceptLabel" />
          </b-card>

          <div v-if="initialLoad && loading">
            <b-card class="mb-3 text-center">
              <b-spinner label="Loading" type="grow" class="m-2 bookapp-action-tint-color" />
            </b-card>
          </div>

          <b-card
            v-if="response && response.giftCardRequest.showField"
            :title="$t('general.giftcard')"
            class="mb-6"
          >
            <b-errors :errors="response.giftCardRequest.errors" class="mb-3" />

            <form @submit.prevent="giftCardRequest">
              <UFormGroup :prepend="$t('general.code')">
                <div class="w-full flex items-center space-x-2">
                  <UInput v-model="giftCardCode" size="lg" class="w-full"  :ui="{ leading: { padding: { lg: 'ps-20'}}}">
                    <template #leading>
                      <p class="text-secondary/50 mr-3 pr-3 border-r border-secondary">
                        {{ $t('general.code') }}
                      </p>
                    </template>
                  </UInput>

                  <UButton class="flex-shrink-0" type="submit" size="lg" :loading="giftCardLoading">
                    {{ $t('pages.book.confirm.applyVoucherGiftCard') }}
                  </UButton>
                </div>
              </UFormGroup>
            </form>

            <template v-if="response.payment.giftCards && response.payment.giftCards.length > 0">
              <div v-for="card in response.payment.giftCards" :key="card.displayNumber" class="mt-3 mb-1 w-full flex items-center justify-between text-secondary p-3 rounded bg-secondary text-sm">
                <div class="flex items-center space-x-3  rtl:space-x-reverse">
                  <b-icon icon="voucher" class="text-primary" />
                  <p><span class="font-semibold">{{ $t('general.giftcard') }}:</span>{{ card.displayNumber }}</p>
                </div>

                <div class="flex items-center space-x-3  rtl:space-x-reverse">
                  <p><span class="font-semibold">{{ $t('general.balance') }}: </span>{{ card.balanceLabel }}</p>

                  <a class="cursor-pointer hover:text-primary" @click="removeGiftCard(card)">
                    <b-icon icon="x" />
                  </a>
                </div>
              </div>
            </template>
          </b-card>

          <b-card
            v-if="response"
            :title="$t('general.payments')"
            content-class=""
            class="mb-6"
          >
            <div v-if="response.payment.splitPayments && response.payment.splitPayments.payments.length" class="mt-8">
              <h6 class="text-primary font-semibold mb-3">
                {{ $t('general.payments') }}
              </h6>

              <div v-for="(payment, index) in response.payment.splitPayments.payments" :key="index" class="mt-3 mb-1 w-full flex items-center justify-between text-secondary p-4 rounded bg-primary text-sm">
                <div class="flex items-center space-x-3  rtl:space-x-reverse">
                  <b-icon icon="voucher" class="text-primary" />
                  <p><span class="font-semibold">{{ payment.typeLabel }}:</span>{{ payment.label }}</p>
                </div>

                <div class="flex items-center space-x-3  rtl:space-x-reverse">
                  <p class="font-semibold">
                    {{ payment.amountLabel }}
                  </p>
                </div>
              </div>
            </div>

            <div v-if="response && response.payment && response.payment.methods">
              <PaymentForm
                :confirm-button-title="$t('general.buyNow')"
                :methods="response.payment.methods"
                :label="pass.name"
                :amount="response.payment.splitPayments.remainder"
                :amount-label="response.payment.splitPayments.remainderLabel"
                :customer="customer"
                :fallback-route="externalFallbackRouteCallback"
                :action="actionCallback"
                :processing="processingPayment"
                :disabled="!isValid"
                @callback="paymentCallback"
              />
            </div>
            <div v-else>
              <b-button
                type="button"
                class="w-full mt-6"
                variant="action"
                :loading="confirming"
                :disabled="confirming"
                @click="confirm"
              >
                {{ $t('general.buy') }}
              </b-button>
            </div>
          </b-card>
        </div>
      </b-summary>
    </b-container>
  </b-layout>
</template>

<script>
import PassDetail from '@/components/pass/elements/Detail.vue'
import PaymentForm from '@/components/payments/PaymentForm'
import Utils from '@/utils'

export default defineNuxtComponent({
  components: {
    PassDetail,
    PaymentForm
  },
  data () {
    return {
      errors: [],
      loading: false,
      initialLoad: true,
      response: null,
      giftCardCode: null,
      giftCardLoading: false,
      processingPayment: false,
      confirming: false,
      confirmData: {
        termsAccepted: false,
        payment: {
          data: null,
          params: null,
          giftCards: []
        }
      }
    }
  },
  async asyncData () {
    const route = useRoute()
    const passStore = usePassStore()

    await passStore.setFromUrlQuery(route.query)

    return {}
  },
  computed: {
    breadcrumbs () {
      return [
        { text: this.$site.tabs.getName('pass', 'Pass'), to: { path: '/pass' } },
        { text: this.pass?.name || 'Pass', active: true },
        { text: this.$t('general.customer'), to: { path: '/pass/customer', query: { ...this.$store.getters['pass/urlQuery'], override: true } } },
        { text: this.$t('general.confirm'), active: true }
      ]
    },
    pass () {
      return this.$store.getters['pass/pass']
    },
    customer () {
      return this.$store.getters['pass/customer']
    },
    isValid () {
      return (!this.response.terms.required || (this.response.terms.required && this.confirmData.termsAccepted))
    }
  },
  mounted () {
    this.confirmData.termsAccepted = (parseInt(this.$route.query.terms) === 1)

    this.loadData()

    if (this.pass && !this.pass.allowInternet) {
      this.$router.replace({ path: '/pass' })
    }
  },
  methods: {
    formatHtml (text) {
      return Utils.text.formatHtml(text)
    },
    removeGiftCard (giftCard) {
      for (let i = 0; i < this.confirmData.giftCards.length; i++) {
        if (this.confirmData.giftCards[i] === giftCard) {
          this.confirmData.giftCards.splice(i, 1)
        }
      }

      this.loadData()
    },
    async giftCardRequest () {
      this.giftCardLoading = true
      await this.loadData()
      this.giftCardLoading = false
    },
    async loadData () {
      this.loading = true

      const response = await this.$api.pass.purchase(this.$api.ACTION_INFO, this.customer, this.pass, this.confirmData.termsAccepted, this.confirmData.giftCards, this.giftCardCode, null, null, { })

      if (response.success) {
        this.response = null

        this.response = response

        this.giftCardCode = null

        this.confirmData.giftCards = this.response.payment.giftCards
      } else if (response.errors) {
        this.errors = response.errors
      }

      this.loading = false
      this.initialLoad = false
    },
    async confirm () {
      if (this.response.terms.required && !this.confirmData.termsAccepted) {
        this.errors = ['You must accept ' + this.response.terms.title]

        this.$nextTick(() => {
          // this.scrollTo(this.$refs.terms)
          this.$refs?.terms?.$el?.scrollIntoView({ behavior: 'smooth' })
        })

        return false
      }

      this.confirming = true

      const response = await this.$api.pass.purchase(this.$api.ACTION_CONFIRM, this.customer, this.pass, this.confirmData.termsAccepted, this.confirmData.giftCards, this.giftCardCode, null, null, {})

      if (response.success) {
        const customerPass = response.customerPass

        this.$router.push({ path: '/pass/complete', query: { id: customerPass.id, signature: customerPass.signature } })
      } else if (response.errors) {
        this.errors = response.errors
      }
      this.confirming = false
    },
    externalFallbackRouteCallback (method) {
      const urlQuery = this.$store.getters['pass/urlQuery']

      urlQuery.terms = 1

      return {
        path: '/pass/confirm',
        query: urlQuery
      }
    },
    actionCallback (method, params) {
      return this.$api.pass.purchase(this.$api.ACTION_INTEGRATION, this.customer, this.pass, this.confirmData.termsAccepted, this.confirmData.giftCards, this.giftCardCode, method.data, params, {})
    },
    async paymentCallback (method, { success, params, errors }) {
      if (success) {
        this.processingPayment = true
        const response = await this.$api.pass.purchase(this.$api.ACTION_CONFIRM, this.customer, this.pass, this.confirmData.termsAccepted, this.confirmData.giftCards, this.giftCardCode, method.data, params, {})

        if (response.success) {
          const customerPass = response.customerPass

          this.$router.push({ path: '/pass/complete', query: { id: customerPass.id, signature: customerPass.signature } })
        } else {
          this.processingPayment = false
          this.errors = response.errors
        }
      } else {
        this.errors = errors
      }
    }
  }
})
</script>
